import { removeDevModeFilterNote } from '@/lib/ai/code-review-service/utils/dev-mode-filter-note';
import { parsePatchRows } from '@/lib/diff/parse-patch-rows';
import { removeReplyFooter } from '@/lib/pr-v2/issue-comment-reply';
import { asRecord } from '@/lib/pr-v2/utils/as-record';

const PROMPT_DETAILS_BLOCK_REGEX =
  /<details>\s*<summary>\s*Prompt for AI agents(?:\s*\((?:all|unresolved)\s+issues\))?\s*<\/summary>[\s\S]*?<\/details>/gi;
const PROMPT_DETAILS_CONTENT_REGEX =
  /<details>\s*<summary>\s*Prompt for AI agents(?:\s*\((?:all|unresolved)\s+issues\))?\s*<\/summary>([\s\S]*?)<\/details>/i;
// Match the embedded provider fix button for any provider (e.g. "Fix with cubic",
// "Fix with Cursor"); the alt text is provider-specific so we accept any label.
const FIX_BUTTON_ANCHOR_REGEX =
  /<a(?:\s[^>]*)?>\s*(?:<picture(?:\s[^>]*)?>[\s\S]*?<img(?:\s[^>]*)?alt=(?:"|')Fix with [^"']+(?:"|')[^>]*>[\s\S]*?<\/picture>|<img(?:\s[^>]*)?alt=(?:"|')Fix with [^"']+(?:"|')[^>]*>)\s*<\/a>/gi;
const FIX_BUTTON_MARKDOWN_LINK_REGEX = /\[Fix with [^\]]+\]\([^)]*\)/gi;

function isMentionOnlyLine(line: string): boolean {
  const tokens = line.trim().split(/\s+/u).filter(Boolean);
  return tokens.length > 0 && tokens.every((token) => token.startsWith('@'));
}

export function formatQuotedText(text: string): string {
  return text
    .split('\n')
    .map((line) => `> ${line}`)
    .join('\n');
}

export function removeInitialQuotedLinesAndFooter(content: string): string {
  const trimmedContent = removeReplyFooter(content);
  if (!trimmedContent) {
    return '';
  }

  const lines = trimmedContent.split(/\r?\n/u);
  let scanIndex = 0;

  while (scanIndex < lines.length) {
    const line = lines[scanIndex]?.trim() ?? '';
    if (!line || isMentionOnlyLine(line)) {
      scanIndex += 1;
      continue;
    }
    break;
  }

  if (!lines[scanIndex]?.trimStart().startsWith('>')) {
    return trimmedContent;
  }

  let quoteEndIndex = scanIndex;
  while (quoteEndIndex < lines.length && lines[quoteEndIndex]?.trimStart().startsWith('>')) {
    quoteEndIndex += 1;
  }

  while (quoteEndIndex < lines.length && !(lines[quoteEndIndex]?.trim() ?? '')) {
    quoteEndIndex += 1;
  }

  return [...lines.slice(0, scanIndex), ...lines.slice(quoteEndIndex)].join('\n').trim();
}

export function sanitizeMrgeBotComment(content: string): string {
  let sanitized = removeDevModeFilterNote(content);

  sanitized = sanitized.replace(/\. View (?:it|them) in \[(?:mrge\.io|cubic\.dev)\]\(.*?\)/g, '');

  // Strip "Prompt for AI agents" details section
  sanitized = sanitized.replace(PROMPT_DETAILS_BLOCK_REGEX, '');

  // Strip hidden metadata HTML comments (<!-- metadata:{...} -->, <!-- cubic:* -->, etc.)
  sanitized = sanitized.replace(/<!--[\s\S]*?-->\n?/g, '');

  // Strip [internal] metadata lines
  sanitized = sanitized.replace(/^\[internal\].*$/gm, '');

  // Strip "View Feedback" markdown links: [View Feedback](url) or View Feedback: [1](url) [2](url)
  sanitized = sanitized.replace(/\[View Feedback\]\([^)]*\)/g, '');
  sanitized = sanitized.replace(/View Feedback:\s*(?:\[\d+\]\([^)]*\)\s*)+/g, '');

  // Strip provider fix button HTML link blocks (<a>...<picture>...<img alt="Fix with ...">...</a>)
  sanitized = sanitized.replace(FIX_BUTTON_ANCHOR_REGEX, '');
  sanitized = sanitized.replace(FIX_BUTTON_MARKDOWN_LINK_REGEX, '');

  // Strip standalone "Fix with <provider>" or "View Feedback" text
  sanitized = sanitized.replace(/^[ \t]*(?:Fix with [^\n]*\S|View Feedback)[ \t]*$/gm, '');

  // Strip standalone horizontal rules (---) used as separators
  sanitized = sanitized.replace(/^\s*---\s*$/gm, '');

  // Clean up excessive blank lines left after stripping
  sanitized = sanitized.replace(/\n{3,}/g, '\n\n');

  return sanitized.trim();
}

function extractFirstCodeFenceContent(content: string): string | null {
  const lines = content.split(/\r?\n/u);
  let openFence: { character: '`' | '~'; length: number; startIndex: number } | null = null;

  for (const [index, rawLine] of lines.entries()) {
    const trimmedLine = rawLine.trimStart();
    const firstCharacter = trimmedLine[0];
    if (firstCharacter !== '`' && firstCharacter !== '~') {
      continue;
    }

    let fenceLength = 0;
    while (trimmedLine[fenceLength] === firstCharacter) {
      fenceLength += 1;
    }

    if (fenceLength < 3) {
      continue;
    }

    const fenceRemainder = trimmedLine.slice(fenceLength);

    if (!openFence) {
      if (firstCharacter === '`' && fenceRemainder.includes('`')) {
        continue;
      }

      openFence = {
        character: firstCharacter,
        length: fenceLength,
        startIndex: index + 1,
      };
      continue;
    }

    if (fenceRemainder.trim() !== '') {
      continue;
    }

    if (firstCharacter !== openFence.character || fenceLength < openFence.length) {
      continue;
    }

    return lines.slice(openFence.startIndex, index).join('\n').trim() || null;
  }

  return null;
}

export function extractAiAgentPromptText(content: string | null | undefined): string | null {
  if (!content) {
    return null;
  }

  const detailsMatch = content.match(PROMPT_DETAILS_CONTENT_REGEX);
  const detailsContent = detailsMatch?.[1];
  if (!detailsContent) {
    return null;
  }

  return extractFirstCodeFenceContent(detailsContent);
}

function findUnquotedSuggestionRange(lines: string[]): {
  startIndex: number;
  endIndex: number;
} | null {
  let startIndex: number | null = null;

  for (const [index, line] of lines.entries()) {
    const trimmedLine = line.trimStart();
    const isQuotedLine = trimmedLine.startsWith('>');

    if (startIndex === null) {
      if (!isQuotedLine && /^```suggestion[ \t]*$/u.test(trimmedLine)) {
        startIndex = index;
      }
      continue;
    }

    if (!isQuotedLine && /^```[ \t]*$/u.test(trimmedLine)) {
      return { startIndex, endIndex: index };
    }
  }

  return null;
}

export function extractSuggestionParts(content: string): {
  suggestion: string | null;
  regularContent: string;
} {
  const lines = content.split(/\r?\n/u);
  const suggestionRange = findUnquotedSuggestionRange(lines);

  if (suggestionRange) {
    const suggestion = lines
      .slice(suggestionRange.startIndex + 1, suggestionRange.endIndex)
      .join('\n');
    const regularContent = [
      ...lines.slice(0, suggestionRange.startIndex),
      ...lines.slice(suggestionRange.endIndex + 1),
    ]
      .join('\n')
      .trim();

    return { suggestion, regularContent };
  }

  return { suggestion: null, regularContent: content };
}

function asPositiveInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;
}

/**
 * The lines a review comment's suggestion replaces, or undefined when its GitHub payload does not
 * hold all of them. `diff_hunk` ends at the commented line in the commit the comment was written
 * on, so the range comes from the `original_*` positions, which point into that commit.
 */
export function getSuggestionOriginalLines(raw: unknown): string[] | undefined {
  const record = asRecord(raw);
  const diffHunk = record?.diff_hunk;
  const endLine = asPositiveInteger(record?.original_line);
  // GitHub applies suggestions to the right side only.
  if (
    !record ||
    [record.side, record.start_side, record.original_side].includes('LEFT') ||
    typeof diffHunk !== 'string' ||
    !endLine
  ) {
    return undefined;
  }

  const startLine = asPositiveInteger(record.original_start_line) ?? endLine;
  const lines = parsePatchRows(diffHunk)
    .filter((row) => row.newLine !== null && row.newLine >= startLine && row.newLine <= endLine)
    .map((row) => row.text.slice(1));

  return lines.length === endLine - startLine + 1 ? lines : undefined;
}
